import { create } from "@bufbuild/protobuf";
import { signal } from "@preact/signals";
import { cancelRanked, getRankedStatus, startRanked } from "../api/client";
import { isApiError } from "../api/errors";
import { completeRankedStatus } from "../api/complete";
import { GAME_MODES, modeSlug } from "../api/modes";
import { GameMode, RankedState, RankedStatusSchema } from "../api/types";
import type { FullRankedStatus } from "../api/types";
import { startGame } from "./game";
import { toast, toastError } from "./toasts";

function idleStatus(): FullRankedStatus {
  return completeRankedStatus(create(RankedStatusSchema, { state: RankedState.IDLE }));
}

/** Ranked queue state, polled once per second while searching. */
export const rankedStatus = signal<FullRankedStatus>(idleStatus());

/** Mode of the active (or last chosen) ranked search. */
export const rankedMode = signal<GameMode>(GameMode.DEFUSAL);

/** True while a start/cancel request is in flight. */
export const rankedBusy = signal(false);

/** Players per team of a ranked match on this server (the server tells with every status). */
export const rankedTeamSize = signal(4);

function noteTeamSize(status: FullRankedStatus): void {
  if (status.teamSize > 0) rankedTeamSize.value = status.teamSize;
}

const POLL_MS = 1000;
const MAX_POLL_FAILURES = 5;

let pollTimer: number | undefined;
let pollToken = 0; // invalidates in-flight polls after stop/cancel
let cancelEpoch = 0; // bumped by every cancel: start/resume requests that were in flight are stale

function stopPolling(): void {
  pollToken++;
  clearTimeout(pollTimer);
  pollTimer = undefined;
}

function schedulePoll(failures: number): void {
  const mine = pollToken;
  pollTimer = window.setTimeout(async () => {
    pollTimer = undefined;
    try {
      const status = await getRankedStatus();
      if (mine !== pollToken) return;
      noteTeamSize(status);
      if (status.state === RankedState.FOUND && status.match) {
        stopPolling();
        rankedStatus.value = idleStatus();
        startGame(status.match);
        return;
      }
      if (status.state === RankedState.IDLE) {
        // The server dropped us (it saw no status request for too long): the search is over.
        stopPolling();
        rankedStatus.value = status;
        toast("You were removed from the ranked queue. Search again to keep looking.", "error");
        return;
      }
      rankedStatus.value = status;
      if (status.state === RankedState.SEARCHING) schedulePoll(0);
    } catch (e) {
      if (mine !== pollToken) return;
      if (failures + 1 >= MAX_POLL_FAILURES) {
        stopPolling();
        rankedStatus.value = idleStatus();
        toastError(e);
      } else {
        schedulePoll(failures + 1);
      }
    }
  }, POLL_MS);
}

/** Join the ranked queue for `mode` and start polling. */
export async function startRankedSearch(mode: GameMode): Promise<void> {
  if (rankedBusy.value) return;
  rankedBusy.value = true;
  rankedMode.value = mode;
  const epoch = cancelEpoch;
  try {
    const status = await startRanked(mode);
    if (epoch !== cancelEpoch) {
      // The screen was left while the request was in flight: the server queued us, so leave again.
      await cancelRanked();
      return;
    }
    noteTeamSize(status);
    stopPolling();
    rankedStatus.value = status;
    schedulePoll(0);
  } catch (e) {
    if (isApiError(e) && e.code === "already_in_match") {
      // A ranked match of ours is still on: go back into it.
      await resumeRankedSearch();
    } else {
      toastError(e);
    }
  } finally {
    rankedBusy.value = false;
  }
}

/** Leave the queue. Safe to call when not searching. */
export async function cancelRankedSearch(): Promise<void> {
  cancelEpoch++;
  stopPolling();
  const wasSearching = rankedStatus.value.state !== RankedState.IDLE;
  rankedStatus.value = idleStatus();
  if (!wasSearching) return;
  rankedBusy.value = true;
  try {
    await cancelRanked();
  } catch (e) {
    toast(e instanceof Error ? e.message : "Could not leave the queue", "error");
  } finally {
    rankedBusy.value = false;
  }
}

/** Resume polling when the server still has us queued (page reload while searching). */
export async function resumeRankedSearch(): Promise<void> {
  const epoch = cancelEpoch;
  try {
    const status = await getRankedStatus();
    noteTeamSize(status);
    if (status.state === RankedState.SEARCHING && epoch !== cancelEpoch) {
      // Left the screen while this request was in flight: do not pick the queue back up.
      await cancelRanked();
    } else if (status.state === RankedState.SEARCHING) {
      rankedStatus.value = status;
      if (status.mode !== GameMode.UNSPECIFIED) rankedMode.value = status.mode;
      stopPolling();
      schedulePoll(0);
    } else if (status.state === RankedState.FOUND && status.match) {
      startGame(status.match);
    }
  } catch {
    // Idle UI is correct when the status cannot be read.
  }
}

// ---- Quick play mode selection (remembered between visits) ----

const QUICKPLAY_KEY = "gunrun.quickplay.modes";

/** Stored as slugs ("ffa", "tdm", ...) so the value survives enum renumbering. */
function readQuickplayModes(): GameMode[] {
  try {
    const raw = localStorage.getItem(QUICKPLAY_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) {
      const modes = GAME_MODES.filter((m) => parsed.includes(modeSlug(m)));
      if (modes.length > 0) return modes;
    }
  } catch {
    // Unreadable or blocked storage: fall back to every mode.
  }
  return [...GAME_MODES];
}

/** Modes ticked on the Play screen (all four by default), in canonical order. */
export const quickplayModes = signal<GameMode[]>(readQuickplayModes());

export function setQuickplayModes(modes: GameMode[]): void {
  const ordered = GAME_MODES.filter((m) => modes.includes(m));
  quickplayModes.value = ordered;
  try {
    localStorage.setItem(QUICKPLAY_KEY, JSON.stringify(ordered.map(modeSlug)));
  } catch {
    // Not persisted; the selection still applies for this session.
  }
}
